// SupportTickets API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// resolves through the shared HttpClient base. Mirrors the shape of
// externalItemsApi.js (multipart upload) and engagementApi.js (query-string
// list filters).
const withSupportTicketsApi = (Base) => class extends Base {
  // Self-service (student/instructor)
  // ==================================

  async createSupportTicket(payload) {
    return this.request('/support-tickets', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async listMySupportTickets(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/support-tickets/mine${qs ? `?${qs}` : ''}`);
  }

  async getMySupportTicket(ticketId) {
    return this.request(`/support-tickets/mine/${ticketId}`);
  }

  async replySupportTicket(ticketId, message) {
    return this.request(`/support-tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  }

  async uploadSupportTicketAttachment(ticketId, file, messageId) {
    const formData = new FormData();
    formData.append('file', file);
    if (messageId) formData.append('messageId', messageId);

    return this.request(`/support-tickets/${ticketId}/attachments`, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type with boundary
      body: formData
    });
  }

  /**
   * Downloads a ticket attachment as a Blob (the download route needs the
   * Authorization header, so this can't be a plain <a href>, same reasoning
   * as downloadScoresTemplate in externalItemsApi.js).
   */
  async downloadSupportTicketAttachment(ticketId, attachmentId) {
    const token = localStorage.getItem('faithinsight_token');
    const response = await fetch(`${this.baseURL}/support-tickets/${ticketId}/attachments/${attachmentId}/download`, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to download attachment: ${response.statusText}`);
    }

    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const fileName = match ? match[1] : `attachment-${attachmentId}`;

    return { blob: await response.blob(), fileName };
  }

  // Admin
  // =====

  async listSupportTickets(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/support-tickets${qs ? `?${qs}` : ''}`);
  }

  // Instructor
  // ==========

  /** Course Question tickets for courses the instructor teaches. */
  async listTeachingSupportTickets(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/support-tickets/teaching${qs ? `?${qs}` : ''}`);
  }

  async getSupportTicket(ticketId) {
    return this.request(`/support-tickets/${ticketId}`);
  }

  async updateSupportTicketStatus(ticketId, status) {
    return this.request(`/support-tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  async updateSupportTicketPriority(ticketId, priority) {
    return this.request(`/support-tickets/${ticketId}/priority`, {
      method: 'PATCH',
      body: JSON.stringify({ priority })
    });
  }
};

export default withSupportTicketsApi;
